import { useNavigate } from "@tanstack/react-router";
import clsx from "clsx";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import OutsideClickHandler from "react-outside-click-handler";
import Select from "react-select-virtualized";
import { toast } from "sonner";

import { useUIStateStore } from "@/store/uiStateStore";

// import AppState from "../store/appstate"; // This is the old Pullstate store
import useAppStore, { persistAccountToLocalStorage } from "../store/appStateZustand"; // Corrected import for Zustand store
import Avatar from "./Avatar";
import LoadingSmall from "./LoadingSmall";

function UserDropDown(props) {
  const { t } = useTranslation();
  const { data, logout, accounts, selectedAccount, siteConfig, isAccountSwitching } = props;

  const [showMenu, setShowMenu] = useState(false);
  const [showSwitch, setShowSwitch] = useState(false);
  const navigate = useNavigate();

  const username = useAppStore(s => s.authentication?.userName); // Corrected Zustand usage

  const isTunecore = useUIStateStore(state => state.isTunecore);

  const accountsToSort = JSON.parse(JSON.stringify(accounts));

  const accountCode = data?.artistaccountcode || "";

  const handleChange = async (selected) => {
    if (!selected)
      return;

    try {
      // Set loading state via Zustand store
      useAppStore.getState().setAccountSwitching(true);

      // Update account state directly via Zustand store
      useAppStore.setState({
        selectedAccount: selected.value.artistaccountcode,
        selectedCatalogue: selected.value.cataloguecode,
      });

      // Persist to localStorage for cross-session recovery
      persistAccountToLocalStorage(selected.value.artistaccountcode);

      toast.success(`Switched to ${selected.value.accountname}`, {
        description: `Account: ${selected.value.artistaccountcode}`,
        duration: 3000,
      });
    }
    catch (error) {
      console.error("Account switch failed:", error);
      toast.error("Failed to switch account", {
        description: "Please try again",
        duration: 4000,
      });
    }
    finally {
      // Clear loading state
      useAppStore.getState().setAccountSwitching(false);
    }

    setShowMenu(false);
    setShowSwitch(false);
  };
  const userButtonClasses = clsx(
    "flex flex-row w-auto h-full items-center justify-center md:justify-end gap-2 group lg:p-2 relative py-2",
    {
      "text-old-sentric-dark-gray": !isTunecore,
      "text-white font-tunecore": isTunecore,
      "": isTunecore && showMenu,
    },
  );

  const userDropDownClasses = clsx("shadow-2xl min-h-72 pt-2 px-4 rounded-lg min-w-64", {
    "bg-white": !isTunecore,
    "bg-tunecore-dark-element text-white ": isTunecore,
  });

  const customStyles = {
    menu: provided => ({
      ...provided,
      zIndex: 0, // Adjust the z-index as needed
      position: "relative", // Adjust the position as needed
      maxHeight: "300px",
      marginBottom: "16px",
    }),
    // You can add more custom styles for other parts of the component if needed
  };

  return (
    <div className="relative">
      <OutsideClickHandler
        onOutsideClick={() => {
          setShowMenu(false);
        }}
      >
        <button
          onClick={() => setShowMenu(!showMenu)}
          type="button"
          className={userButtonClasses}
          id="menu-button"
          tabIndex="0"
        >
          <span
            className={`line-clamp-2 inline-flex gap-2 max-md:hidden text-left self-center group-hover:underline ${isTunecore ? "tracking-wide text-lg" : "tracking-tight text-base"}`}
          >
            {isAccountSwitching ? (
              <LoadingSmall />
            ) : (
              data?.accountname
            )}
          </span>

          <div>
            <Avatar data={data} />
          </div>
        </button>

        {showMenu && (
          <div className={`absolute right-0 top-full z-50 mt-2 ${userDropDownClasses}`}>
            <div className="user-dropdown-child-border-bottom ">
              <p
                className={`${
                  isTunecore
                    ? "text-white border-none text-base lg:text-sm"
                    : "text-sm border-none "
                } text-left px-2 py-2 wrap-text`}
              >
                <span className="font-bold">
                  {t("common:user-menu-username")}
                  {" "}

                </span>
                {username}
              </p>
            </div>
            <div className="user-dropdown-child-border-bottom ">

              <p className={`${isTunecore ? "text-white border-none text-base lg:text-sm" : "text-sm border-none "} text-left px-2 py-2 wrap-text`}>
                <span className="font-bold">
                  {t("common:sen-code")}
                  :
                  {" "}
                </span>
                {accountCode}
              </p>
            </div>
            <div>
              {accounts?.artistaccounts?.length > 0 && (
                <button
                  id="btnUserSwitchAccount"
                  type="button"
                  onClick={() => setShowSwitch(!showSwitch)}
                  className={`${
                    isTunecore ? "text-white border-none" : "text-gray-700"
                  } block w-full text-left px-4 py-2 text-sm hover:underline`}
                  role="menuitem"
                  tabIndex="0"
                >
                  {t("common:user-menu-switch")}
                </button>
              )}

              {showSwitch && (
                <div
                  className={
                    isTunecore
                      ? "bg-tunecore-dark-element"
                      : "bg-white"
                  }
                >
                  <Select
                    id="txtUserAccountSearch"
                    menuIsOpen
                    className="w-full text-sm text-old-sentric-dark-gray"
                    name="artistAccount"
                    placeholder="Search..."
                    value={selectedAccount}
                    onChange={handleChange}
                    options={accountsToSort?.artistaccounts
                      ?.sort((a, b) => a.accountname.localeCompare(b.accountname))
                      .map(s => ({ label: s.accountname, value: s }))}
                    search
                    styles={customStyles}
                  />
                </div>
              )}
              {!siteConfig.disable_add_account_button && (
                <button
                  id="btnUserAddAccount"
                  type="button"
                  onClick={() => navigate({ to: "/add-account-pre" })}
                  className={`${
                    isTunecore
                      ? "border-none text-white"
                      : "text-gray-700"
                  } block w-full text-left px-4 py-2 text-sm hover:underline user-dropdown-child-border-top`}
                  role="menuitem"
                  tabIndex="0"
                >
                  {t("common:user-menu-add")}
                </button>
              )}
              <button
                id="btnUserSignOut"
                type="button"
                onClick={() => logout()}
                className={`${
                  isTunecore
                    ? "border-none text-white"
                    : " text-gray-700"
                } block w-full text-left px-4 py-2 text-sm mb-4 hover:underline user-dropdown-child-border-top`}
                role="menuitem"
                tabIndex="0"
              >
                {t("common:user-menu-sign-out")}
              </button>
            </div>
          </div>
        )}
      </OutsideClickHandler>
    </div>
  );
}

export default UserDropDown;
